import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { prayersReadingsService } from '../../../../services/prayersReadingsInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PrayersReadingsInput');

type PrayersReadingsInputScreenRouteProp = RouteProp<RootStackParamList, 'PrayersReadingsInput'>;

export const PrayersReadingsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<PrayersReadingsInputScreenRouteProp>();

  const { prayersReadingsData, isEditing } = route.params || {};

  // State for all 10 entity fields
  const [religiousPreference, setReligiousPreference] = useState(
    prayersReadingsData?.religiousPreference || ''
  );
  const [specificPrayers, setSpecificPrayers] = useState(
    prayersReadingsData?.specificPrayers || ''
  );
  const [scriptureReadings, setScriptureReadings] = useState(
    prayersReadingsData?.scriptureReadings || ''
  );
  const [poemsReadings, setPoemsReadings] = useState(
    prayersReadingsData?.poemsReadings || ''
  );
  const [preferredReaders, setPreferredReaders] = useState(
    prayersReadingsData?.preferredReaders || ''
  );
  const [languagePreference, setLanguagePreference] = useState(
    prayersReadingsData?.languagePreference || ''
  );
  const [culturalTraditions, setCulturalTraditions] = useState(
    prayersReadingsData?.culturalTraditions || ''
  );
  const [specialBlessings, setSpecialBlessings] = useState(
    prayersReadingsData?.specialBlessings || ''
  );
  const [avoidContent, setAvoidContent] = useState(
    prayersReadingsData?.avoidContent || ''
  );
  const [notes, setNotes] = useState(
    prayersReadingsData?.notes || ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // At least one field should be filled
    if (!religiousPreference && !specificPrayers && !scriptureReadings &&
        !poemsReadings && !preferredReaders && !languagePreference &&
        !culturalTraditions && !specialBlessings && !avoidContent && !notes) {
      Alert.alert('Error', 'Please fill in at least one field');
      return;
    }

    try {
      setSaving(true);

      const prayersReadingsUpdateData = {
        religiousPreference: religiousPreference.trim() || undefined,
        specificPrayers: specificPrayers.trim() || undefined,
        scriptureReadings: scriptureReadings.trim() || undefined,
        poemsReadings: poemsReadings.trim() || undefined,
        preferredReaders: preferredReaders.trim() || undefined,
        languagePreference: languagePreference.trim() || undefined,
        culturalTraditions: culturalTraditions.trim() || undefined,
        specialBlessings: specialBlessings.trim() || undefined,
        avoidContent: avoidContent.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      let savedPrayersReadings;

      if (isEditing && prayersReadingsData?.id) {
        // Update existing record
        savedPrayersReadings = await prayersReadingsService.updatePrayersReadings(
          prayersReadingsData.id,
          prayersReadingsUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedPrayersReadings?.data || savedPrayersReadings;
        logger.debug('Prayers and readings updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('PrayersReadingsLockUnlock', { prayersReadingsId: updatedData.id });
      } else {
        // Create new record
        savedPrayersReadings = await prayersReadingsService.createPrayersReadings(prayersReadingsUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedPrayersReadings?.data || savedPrayersReadings;
        logger.debug('Prayers and readings saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('PrayersReadingsLockUnlock', { prayersReadingsId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving prayers and readings:', error);
      Alert.alert('Error', 'Failed to save prayers and readings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Prayers and Readings
        </Text>
        <Text style={styles.subtitle}>
          Document your preferences for prayers, scripture, and readings
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.label}>Religious Preference</Text>
          <TextInput
            style={styles.input}
            placeholder="e.g., Catholic, Jewish, Muslim, Non-religious"
            value={religiousPreference}
            onChangeText={setReligiousPreference}
          />

          <Text style={styles.label}>Specific Prayers</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Enter specific prayers to be said or recited..."
            value={specificPrayers}
            onChangeText={setSpecificPrayers}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Scripture Readings</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Enter Bible verses, scripture, or religious text readings..."
            value={scriptureReadings}
            onChangeText={setScriptureReadings}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Poems & Secular Readings</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Enter poems, secular readings, or literary passages..."
            value={poemsReadings}
            onChangeText={setPoemsReadings}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Preferred Readers</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Who would you like to read the prayers/readings?"
            value={preferredReaders}
            onChangeText={setPreferredReaders}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Language Preference</Text>
          <TextInput
            style={styles.input}
            placeholder="e.g., English, Spanish, Latin, Hebrew"
            value={languagePreference}
            onChangeText={setLanguagePreference}
          />

          <Text style={styles.label}>Cultural Traditions</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Enter cultural or religious traditions to include..."
            value={culturalTraditions}
            onChangeText={setCulturalTraditions}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Special Blessings</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Enter special blessings to be included..."
            value={specialBlessings}
            onChangeText={setSpecialBlessings}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Content to Avoid</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Content, themes, or readings to avoid..."
            value={avoidContent}
            onChangeText={setAvoidContent}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Additional Notes</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Any additional notes or instructions..."
            value={notes}
            onChangeText={setNotes}
            multiline
            textAlignVertical="top"
          />

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    opacity: 0.9,
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(12),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
  },
  textArea: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(10),
    minHeight: s(100),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default PrayersReadingsInputScreen;
